"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { EASE } from "@/lib/motion";
import { Icon, type IconName } from "@/components/ui/Icon";

/* Snackbars, on the kit's Rewards-page frames: a solid tone-coloured card —
   green for a completion, the info blue for something that merely changed,
   red for a failure — carrying an icon, a bold title over a one-line status,
   an optional action link ("Rewards"), and a close. They dismiss themselves;
   the close is for reading faster than eight seconds.

   Fills come through the theme's own tone tokens as inline styles rather than
   generated utilities — one arbitrary selector has silently missed the
   stylesheet before, and a toast that renders unstyled is worse than none. */

const TONE_FILL: Record<ToastTone, string> = {
  success: "var(--color-success)",
  info: "var(--color-info)",
  error: "var(--color-error)",
};

const DISMISS_MS = 8000;

export type ToastTone = "success" | "info" | "error";

export type ToastInput = {
  tone: ToastTone;
  title: string;
  body?: string;
  icon?: IconName;
  /* Where the action link points; the label names the destination. */
  href?: string;
  hrefLabel?: string;
  /* A toast with the key of one already showing replaces it — the
     cashier's "Withdrawal processing..." becoming "Withdrawal successful". */
  key?: string;
  /* How long it stays, in ms. Infinity keeps it until it is replaced or
     dismissed — a transfer still pending. */
  duration?: number;
};

type Toast = ToastInput & { id: number };

type ToastApi = { push: (toast: ToastInput) => void; dismiss: (key: string) => void };

const ToastContext = createContext<ToastApi | null>(null);

export const useToasts = () => {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error("useToasts must be used inside ToastProvider");
  }

  return context;
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const dismissKey = useCallback((key: string) => {
    setToasts((current) => current.filter((toast) => toast.key !== key));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      setToasts((current) =>
        [...current.filter((toast) => input.key === undefined || toast.key !== input.key), { ...input, id }].slice(-4),
      );
      const duration = input.duration ?? DISMISS_MS;
      if (Number.isFinite(duration)) setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss: dismissKey }), [push, dismissKey]);

  return (
    <ToastContext.Provider value={value}>
      {children}

      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-[16px] top-[76px] z-[60] flex flex-col items-center gap-[8px] sm:inset-x-auto sm:right-[16px] sm:items-end"
      >
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              layout
              initial={{ opacity: 0, y: -10, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.25, ease: EASE }}
              className="pointer-events-auto flex w-full max-w-[360px] items-center gap-[12px] rounded-xl px-[16px] py-[12px] text-white shadow-lg"
              style={{ background: TONE_FILL[toast.tone] }}
            >
              <Icon name={toast.icon ?? "gift"} size={22} className="shrink-0" />

              <div className="min-w-0 flex-1">
                <p className="line-clamp-3 text-[13px] font-bold leading-[1.5]">{toast.title}</p>
                {toast.body && (
                  <p className="text-[12px] leading-[1.5] opacity-90">{toast.body}</p>
                )}
              </div>

              {toast.href && (
                <Link
                  href={toast.href}
                  onClick={() => dismiss(toast.id)}
                  className="shrink-0 rounded-lg px-[10px] py-[6px] text-[12px] font-bold leading-[1.5] transition-colors hover:bg-white/15"
                >
                  {toast.hrefLabel ?? "View"}
                </Link>
              )}

              <button
                type="button"
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss"
                className="grid h-[24px] w-[24px] shrink-0 place-items-center rounded-md transition-colors hover:bg-white/15"
              >
                <Icon name="close" size={14} />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
